/**
 * useIdCaptureGuide — live guidance for the "ID Verification" screen: tells
 * the candidate, in real time, whether they're positioned correctly before
 * they click Capture.
 *
 * Important limits, stated plainly: this is a heuristic, not real ID
 * verification. It confirms (a) exactly one face is in frame and (b) a hand
 * is visible in frame at the same time — a stand-in for "holding something
 * up next to your face". It has no way to recognize an ID card specifically,
 * read its content, or confirm it's a genuine/valid document — no free,
 * self-hosted model does that; real document verification needs a paid
 * third-party service. "ready" means "looks like you're holding something
 * up", not "your ID has been confirmed".
 *
 * Reuses the same self-hosted MediaPipe Face Detector as useIntegrityMonitor,
 * plus a Hand Landmarker (same model family/license — Apache-2.0 — and the
 * same WASM runtime), self-hosted from /public for the same reasons
 * documented in useIntegrityMonitor.ts (no external CDN dependency, and
 * Google's own documented CDN path for these models has previously 404'd).
 */
import { useEffect, useRef, useState } from 'react'

export type IdCaptureStatus = 'loading' | 'no_face' | 'multiple_faces' | 'need_id' | 'ready' | 'unavailable'

const ID_CAPTURE_CONFIG = {
  POLL_INTERVAL_MS: 500,
  MIN_FACE_CONFIDENCE: 0.5,
  // A stalled (not outright failed) fetch of the WASM/model files — a slow
  // network, a proxy or ad-blocker holding the connection open — would
  // otherwise leave the load promise pending forever, status stuck on
  // 'loading', and Capture permanently unusable with no way for the
  // candidate to proceed. This bounds the wait so it always resolves one
  // way or the other.
  // ~18.5MB combined (the WASM runtime alone is ~11MB, the hand-landmarker
  // model another ~7.5MB) - 12s was too tight for anything short of a fast,
  // warm-cache connection and tripped this into 'unavailable' routinely on
  // slower/first-visit networks.
  MODEL_LOAD_TIMEOUT_MS: 25000,
}

const MEDIAPIPE_WASM_BASE_PATH = '/mediapipe/wasm'
const FACE_DETECTOR_MODEL_PATH = '/mediapipe/models/blaze_face_short_range.tflite'
const HAND_LANDMARKER_MODEL_PATH = '/mediapipe/models/hand_landmarker.task'

const STATUS_MESSAGES: Record<IdCaptureStatus, string> = {
  loading: 'Starting camera checks…',
  no_face: "We can't see your face clearly — move fully into frame.",
  multiple_faces: 'Only one person should be in frame.',
  need_id: 'Please hold your ID card up next to your face.',
  ready: 'Looks good — you can capture now.',
  unavailable: "Live guidance isn't available in this browser — go ahead and capture manually.",
}

export default function useIdCaptureGuide(videoEl: HTMLVideoElement | null, active: boolean) {
  const [status, setStatus] = useState<IdCaptureStatus>('loading')
  const faceDetectorRef = useRef<any>(null)
  const handLandmarkerRef = useRef<any>(null)
  const modelsReadyRef = useRef(false)
  const loadingRef = useRef(false)
  const faceTimestampRef = useRef(0)
  const handTimestampRef = useRef(0)

  useEffect(() => {
    if (!active) { setStatus('loading'); return }
    if (loadingRef.current || modelsReadyRef.current) return
    loadingRef.current = true
    let cancelled = false
    const loadModels = async () => {
      const visionModule = await import('@mediapipe/tasks-vision')
      const vision = await visionModule.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_BASE_PATH)
      return Promise.all([
        visionModule.FaceDetector.createFromOptions(vision, {
          baseOptions: { modelAssetPath: FACE_DETECTOR_MODEL_PATH },
          runningMode: 'VIDEO',
          minDetectionConfidence: ID_CAPTURE_CONFIG.MIN_FACE_CONFIDENCE,
        }),
        visionModule.HandLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: HAND_LANDMARKER_MODEL_PATH },
          runningMode: 'VIDEO',
          numHands: 2,
        }),
      ])
    }
    // Deliberately NOT a Promise.race([loadModels(), timeout]) - racing them
    // discards whichever side loses, so a load that was merely slow (and
    // resolves a moment after the timeout) would be thrown away and status
    // left stuck on 'unavailable' for the rest of the session even though
    // guidance had genuinely become available. The timeout below only ever
    // decides what to show meanwhile; it never cancels or supersedes the
    // real load, which keeps running and is still used the moment it settles.
    let timedOut = false
    const timeoutId = setTimeout(() => {
      if (cancelled || modelsReadyRef.current) return
      timedOut = true
      setStatus('unavailable')
    }, ID_CAPTURE_CONFIG.MODEL_LOAD_TIMEOUT_MS)
    loadModels()
      .then(([faceDetector, handLandmarker]) => {
        if (cancelled) return
        faceDetectorRef.current = faceDetector
        handLandmarkerRef.current = handLandmarker
        modelsReadyRef.current = true
        // Recovers from a false 'unavailable' shown while this was still
        // pending - back to 'loading' so the next detection poll tick sets
        // the real status instead of leaving the stale manual-capture message up.
        if (timedOut) setStatus('loading')
      })
      .catch((err) => {
        console.error('ID capture guide: model load failed.', err)
        if (!cancelled) setStatus('unavailable')
      })
      .finally(() => { loadingRef.current = false })
    return () => { cancelled = true; clearTimeout(timeoutId) }
  }, [active])

  useEffect(() => {
    if (!active || !videoEl) return
    const interval = setInterval(() => {
      if (!modelsReadyRef.current || !videoEl.videoWidth) return
      try {
        const faceTimestamp = Math.max(Math.round(performance.now()), faceTimestampRef.current + 1)
        faceTimestampRef.current = faceTimestamp
        const faceResult = faceDetectorRef.current.detectForVideo(videoEl, faceTimestamp)
        const faces = (faceResult && faceResult.detections) || []

        if (faces.length === 0) { setStatus('no_face'); return }
        if (faces.length > 1) { setStatus('multiple_faces'); return }

        const handTimestamp = Math.max(Math.round(performance.now()), handTimestampRef.current + 1)
        handTimestampRef.current = handTimestamp
        const handResult = handLandmarkerRef.current.detectForVideo(videoEl, handTimestamp)
        const hands = (handResult && handResult.landmarks) || []

        setStatus(hands.length > 0 ? 'ready' : 'need_id')
      } catch (err) {
        console.error('ID capture guide: detection call failed.', err)
      }
    }, ID_CAPTURE_CONFIG.POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [active, videoEl])

  return { status, message: STATUS_MESSAGES[status] }
}
